import React, { useState, useContext } from "react";

import { useNavigate } from "react-router-dom";

import { Container, Paper, Title, Text, Group, Stack } from "@mantine/core";
import { Button, TextInput } from "@mantine/core";
import { IconMail, IconCheck, IconX, IconAlertTriangle } from "@tabler/icons-react";
import { AllFatherContext } from "../../../App";

import { useTranslation } from "react-i18next";

import { useForm } from "@mantine/form";

import "./styles.scss";
import { PerformRequest } from "../../../Lib/usePerformRequest";
import { validateEmail } from "../../../Lib/methods";
import { Endpoints } from "../../../Lib/endpoints";
import LanguageSelector from "../../../Lib/i18n/LanguageSelector";
import { getSiteName } from "../../../Lib/siteContent";

const RequestResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const emailForm = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) => (validateEmail(value) ? null : t("auth.resetPassword.emailLabelError")),
    },
  });

  const context = useContext(AllFatherContext);
  const siteName = getSiteName(context?.assets);
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState<number | null>(null);

  const SendEmailReset = async () => {
    const { hasErrors } = emailForm.validate();
    if (!hasErrors) {
      setLoading(true);
      setStatus(null);
      const { email } = emailForm.values;
      const r = await PerformRequest({
        method: Endpoints.GetRequestResetPassword().method,
        route: Endpoints.GetRequestResetPassword().route,
        data: { email },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      setStatus(r?.status);
    }
  };

  let cardContent = null;
  if (status === 201) {
    cardContent = (
      <Paper className="login-form" radius="lg" p="xl" withBorder>
        <Group justify="center" mb="md">
          <IconCheck size={40} color="#22c55e" />
        </Group>
        <Title order={3} ta="center" mb="xs">
          {t("auth.resetPassword.successTitle")}
        </Title>
        <Text ta="center" c="dimmed">
          {t("auth.resetPassword.successMessage")}
        </Text>
        <Button mt="lg" fullWidth radius="md" onClick={() => navigate("/login")}>
          {t("auth.resetPassword.backToLogin")}
        </Button>
      </Paper>
    );
  } else if (status === 404) {
    cardContent = (
      <Paper className="login-form" radius="lg" p="xl" withBorder>
        <Group justify="center" mb="md">
          <IconAlertTriangle size={40} color="#f59e42" />
        </Group>
        <Title order={3} ta="center" mb="xs">
          {t("auth.resetPassword.notFoundTitle")}
        </Title>
        <Text ta="center" c="dimmed">
          {t("auth.resetPassword.notFoundMessage")}
        </Text>
        <Button mt="lg" fullWidth radius="md" onClick={() => setStatus(null)}>
          {t("common.retry")}
        </Button>
      </Paper>
    );
  } else if (status === 500) {
    cardContent = (
      <Paper className="login-form" radius="lg" p="xl" withBorder>
        <Group justify="center" mb="md">
          <IconX size={40} color="#ef4444" />
        </Group>
        <Title order={3} ta="center" mb="xs">
          {t("auth.resetPassword.errorTitle")}
        </Title>
        <Text ta="center" c="dimmed">
          {t("auth.resetPassword.errorMessage")}
        </Text>
        <Button mt="lg" fullWidth radius="md" onClick={() => setStatus(null)}>
          {t("common.retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <div className="login-page">
      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 2000 }}>
        <LanguageSelector />
      </div>
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>
      <Container size="sm" className="login-container">
        <div className="login-content">
          <div className="logo-section">
            <img src={context?.assets?.logos?.legacy} alt={siteName} className="logo" />
            <Title order={1} className="app-title">
              {siteName}
            </Title>
            <Text className="welcome-text" size="lg" c="dimmed">
              {t("auth.resetPassword.title")}
            </Text>
          </div>
          {cardContent ? (
            cardContent
          ) : (
            <Paper className="login-form" radius="lg" p="xl" withBorder>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  SendEmailReset();
                }}
              >
                <Stack gap="lg">
                  <Text ta="center" c="dimmed" mb="sm">
                    {t("auth.resetPassword.subtitle")}
                  </Text>
                  <TextInput
                    name="email"
                    leftSection={<IconMail size={16} />}
                    label={t("auth.resetPassword.emailLabel")}
                    placeholder={t("auth.resetPassword.emailLabel")}
                    size="md"
                    radius="md"
                    variant="filled"
                    {...emailForm.getInputProps("email")}
                    classNames={{
                      input: "form-input",
                      label: "form-label",
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    radius="md"
                    loading={isLoading}
                    className="login-button"
                  >
                    {t("auth.resetPassword.submit")}
                  </Button>
                  <Button variant="subtle" fullWidth mt="xs" onClick={() => navigate("/login")}>
                    {t("auth.resetPassword.backToLogin")}
                  </Button>
                </Stack>
              </form>
            </Paper>
          )}
        </div>
      </Container>
    </div>
  );
};

export default RequestResetPassword;
